import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  Box,
  useMediaQuery,
  useTheme,
  Collapse,
} from '@mui/material';
import {
  Dashboard,
  AccessTime,
  BeachAccess,
  Schedule,
  Work,
  Receipt,
  People,
  Security,
  Business,
  Campaign,
  Assessment,
  ExpandLess,
  ExpandMore,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { usePermissions } from '../../context/PermissionContext';

function DrawerComponent({ drawerWidth, mobileOpen, onDrawerToggle }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      onDrawerToggle();
    }
  };

  const menuItems = [
    {
      title: 'Dashboard',
      icon: <Dashboard />,
      path: '/',
      permission: null,
    },
    {
      title: 'Attendance',
      icon: <AccessTime />,
      section: 'attendance',
      items: [
        {
          title: 'My Attendance',
          path: '/attendance',
          permission: 'attendance:view_own',
        },
        {
          title: 'Approvals',
          path: '/attendance/approvals',
          permission: 'attendance:approve_correction',
        },
      ],
    },
    {
      title: 'Leave',
      icon: <BeachAccess />,
      section: 'leave',
      items: [
        {
          title: 'My Leaves',
          path: '/leave',
          permission: 'leave:view_own',
        },
        {
          title: 'Approvals',
          path: '/leave/approvals',
          permission: 'leave:approve_request',
        },
      ],
    },
    {
      title: 'Overtime',
      icon: <Schedule />,
      section: 'overtime',
      items: [
        {
          title: 'My Overtime',
          path: '/overtime',
          permission: 'overtime:view_own',
        },
        {
          title: 'Approvals',
          path: '/overtime/approvals',
          permission: 'overtime:approve_request',
        },
      ],
    },
    {
      title: 'Service Recording',
      icon: <Work />,
      section: 'service',
      items: [
        {
          title: 'My Records',
          path: '/service/records',
          permission: 'service:view_own',
        },
        {
          title: 'Templates',
          path: '/service/templates',
          permission: 'service:manage_templates',
        },
      ],
    },
    {
      title: 'Payroll',
      icon: <Receipt />,
      section: 'payroll',
      items: [
        {
          title: 'My Payslips',
          path: '/payslips',
          permission: 'payroll:view_own',
        },
        {
          title: 'Manage Payroll',
          path: '/payroll/manage',
          permission: 'payroll:compute',
        },
      ],
    },
  ];

  const adminItems = [
    {
      title: 'User Management',
      icon: <People />,
      path: '/admin/users',
      permission: 'users:view_all',
    },
    {
      title: 'Role Management',
      icon: <Security />,
      path: '/admin/roles',
      permission: 'roles:view_all',
    },
    {
      title: 'Client Management',
      icon: <Business />,
      path: '/admin/clients',
      permission: 'clients:view_all',
    },
    {
      title: 'Announcements',
      icon: <Campaign />,
      path: '/admin/announcements',
      permission: 'announcements:view',
    },
    {
      title: 'Audit Logs',
      icon: <Assessment />,
      path: '/admin/audit-logs',
      permission: 'audit:view',
    },
  ];

  const renderMenuItem = (item) => {
    if (item.permission && !hasPermission(item.permission)) {
      return null;
    }

    if (item.section) {
      // Check if any sub-items are visible
      const visibleItems = item.items.filter(
        (subItem) => !subItem.permission || hasPermission(subItem.permission)
      );

      if (visibleItems.length === 0) {
        return null;
      }

      return (
        <Box key={item.section}>
          <ListItem disablePadding>
            <ListItemButton onClick={() => toggleSection(item.section)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.title} />
              {openSections[item.section] ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={openSections[item.section]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.items.map((subItem) => {
                if (subItem.permission && !hasPermission(subItem.permission)) {
                  return null;
                }
                return (
                  <ListItem key={subItem.path} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === subItem.path}
                      onClick={() => handleNavigation(subItem.path)}
                    >
                      <ListItemIcon>
                        <CheckCircle fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={subItem.title} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </Box>
      );
    }

    return (
      <ListItem key={item.path} disablePadding>
        <ListItemButton
          selected={location.pathname === item.path}
          onClick={() => handleNavigation(item.path)}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.title} />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box>
      <Toolbar />
      <List>
        {menuItems.map((item) => renderMenuItem(item))}
      </List>
      <Divider />
      <List>
        {adminItems.map((item) => renderMenuItem(item))}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
}

export default DrawerComponent;
